import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useAuthenticateWithCookieMutation } from 'src/shared/graphql/generated';
import { useEffectOnMount } from 'src/shared/hooks';
import { isNetworkError } from 'src/shared/utils/graphql';
import { loginPathWithRedirectTo } from 'src/shared/utils/routing';

export const useAuthenticateWithCookie = () => {
  const history = useHistory();
  const [authenticated, setAuthenticated] = useState(false);

  const [authenticateWithCookie, { data }] =
    useAuthenticateWithCookieMutation();

  const { pathname } = useLocation();

  useEffectOnMount(() => {
    const authenticate = async () => {
      // TODO: Optimize this once we can include cookie authentication in the RefreshLink logic.
      // Users will not always have a valid set of JWTs. In that case, instead of using the
      // RefreshLink to try and update their JWTs, we can use the cookie directly. This way,
      // if they don't have a valid cookie they just login normally.
      await defaultLocalStorageAdapter.removeAccessToken();
      await defaultLocalStorageAdapter.removeRefreshToken();

      try {
        await authenticateWithCookie();
      } catch (error) {
        if (!isNetworkError(error)) {
          history.push(loginPathWithRedirectTo(pathname));
        }

        captureException(error);
      }
    };

    void authenticate();
  });

  useEffect(() => {
    if (!data) return;

    const setTokens = async () => {
      const { accessToken, refreshToken } = data.authenticateWithCookie.jwt;

      await defaultLocalStorageAdapter.setAccessToken(accessToken);
      await defaultLocalStorageAdapter.setRefreshToken(refreshToken);
      setAuthenticated(true);
    };

    void setTokens();
  }, [data]);

  return authenticated;
};
